import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { useDarkMode } from '../../../../DarkModeContext';

const EditTeamModal = ({ isOpen, onClose, onSave, teamData, coaches, sports, teamCategories }) => {
  const [teamName, setTeamName] = useState('');
  const [selectedCoach, setSelectedCoach] = useState('');
  const [selectedSport, setSelectedSport] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { isDarkMode } = useDarkMode();
  const modalRef = useRef(null);

  useEffect(() => {
    if (teamData) {
      setTeamName(teamData.team_name);
      setSelectedCoach(teamData.coaches[0]?.toString() || '');
      setSelectedSport(teamData.sport);
      setSelectedCategory(teamData.team_category);
    }
  }, [teamData]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const updatedTeamData = {
      ...teamData,
      team_name: teamName,
      coaches: [parseInt(selectedCoach, 10)],
      sport: selectedSport,
      team_category: selectedCategory,
    };

    try {
      const success = await onSave(updatedTeamData);
      if (success) {
        onClose();
        toast.success('Team updated successfully');
      }
    } catch (error) {
      console.error('Error updating team:', error);
      toast.error(`Failed to update team: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const modalClasses = isDarkMode
    ? "bg-[#252540] text-white"
    : "bg-white text-gray-800";

  const inputClasses = isDarkMode
    ? "border border-white bg-[#252F40] text-white focus:border-white focus:ring focus:ring-white focus:ring-opacity-50"
    : "border border-gray-300 bg-white text-gray-900 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50";

  const saveButtonClasses = isDarkMode
    ? "bg-[#7551FF] hover:bg-[#9E8CFC]"
    : "bg-[#f46524] hover:bg-[#f46570]";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div ref={modalRef} className={`p-6 rounded-lg shadow-xl max-w-md w-full ${modalClasses}`}>
        <h2 className="text-xl font-bold mb-4">Edit Team</h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Team Name</label>
            <input
              type="text"
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              className={`mt-1 block w-full rounded-md shadow-sm ${inputClasses}`}
              required
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Coach</label>
            <select
              value={selectedCoach}
              onChange={(e) => setSelectedCoach(e.target.value)}
              className={`mt-1 block w-full rounded-md shadow-sm ${inputClasses}`}
              required
            >
              <option value="">Select a coach</option>
              {coaches.map((coach) => (
                <option key={coach.id} value={coach.id}>
                  {`${coach.fname} ${coach.lname}`}
                </option>
              ))}
            </select>
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Sport</label>
            <select
              value={selectedSport}
              onChange={(e) => setSelectedSport(e.target.value)}
              className={`mt-1 block w-full rounded-md shadow-sm ${inputClasses}`}
              required
            >
              <option value="">Select a sport</option>
              {sports.map((sport) => (
                <option key={sport.id} value={sport.name}>
                  {sport.name}
                </option>
              ))}
            </select>
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Team Category</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className={`mt-1 block w-full rounded-md shadow-sm ${inputClasses}`}
              required
            >
              <option value="">Select a category</option>
              {teamCategories.map((category) => (
                <option key={category.id} value={category.name}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={onClose}
              className={`px-4 py-2 rounded ${
                isDarkMode ? "bg-gray-600 text-white hover:bg-gray-500" : "bg-gray-300 text-gray-800 hover:bg-gray-400"
              }`}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`px-4 py-2 text-white rounded flex items-center ${saveButtonClasses}`}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </>
              ) : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditTeamModal;